import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, LogOut, Gauge, Activity, Cpu, Mail, Shield, ListChecks, Send, Zap, RefreshCw } from 'lucide-react';
import { clearAuth, isSuperAdmin } from '../lib/auth';
import api from '../lib/api';
import UbmSyncDashboard from './ubm/UbmSyncDashboard';
import ParserQueuePage from './cockpit/ParserQueuePage';
import EmailQueuePage from './cockpit/EmailQueuePage';
import EmailJobsPage from './cockpit/EmailJobsPage';
import { JobQueueContent } from './ubm/JobQueuePage';
import MissingBillsJobPanel from './cockpit/MissingBillsJobPanel';
import BudgetBuildsJobPanel from './cockpit/BudgetBuildsJobPanel';
import Bill2RollinCockpit from './cockpit/Bill2RollinCockpit';
import RefundPendingPage from './cockpit/RefundPendingPage';

const COCKPIT_PAGES = [
  { id: 'ubm-sync', label: 'UBM Sync', icon: Activity },
  { id: 'parser-queue', label: 'Parser Queue', icon: Cpu },
  { id: 'email-queue', label: 'Email Queue', icon: Mail },
  { id: 'email-jobs', label: 'Email Jobs', icon: Send },
  { id: 'job-queue', label: 'Job Queue', icon: ListChecks },
  { id: 'bill2rollin', label: 'Bill2Rollin Jobs', icon: Zap },
];

export default function Cockpit() {
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('ubm-sync');
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const { data } = await api.get('/super-admin/accounts');
        setAccounts(data);
      } catch {
        console.error('Failed to load accounts');
      }
    };
    fetchAccounts();
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="cockpit-page">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Gauge className="h-6 w-6" strokeWidth={2} />
            <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Cockpit
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {isSuperAdmin() && (
              <Button variant="ghost" size="sm" className="rounded-none" onClick={() => navigate('/super-admin')}>
                <Shield className="h-4 w-4 mr-2" /> Super Admin
              </Button>
            )}
            <Button variant="ghost" size="sm" className="rounded-none" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
            </Button>
            <Button variant="ghost" size="sm" className="rounded-none" onClick={() => { clearAuth(); window.location.href = '/login'; }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Sub-page navigation */}
      <nav className="border-b border-border bg-card">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center gap-1">
            {COCKPIT_PAGES.map((page) => {
              const Icon = page.icon;
              return (
                <button
                  key={page.id}
                  onClick={() => setActivePage(page.id)}
                  data-testid={`cockpit-nav-${page.id}`}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    activePage === page.id
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {page.label}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-6 md:px-12 py-6">
        {activePage === 'ubm-sync' && <UbmSyncDashboard />}
        {activePage === 'parser-queue' && <ParserQueuePage accounts={accounts} />}
        {activePage === 'email-queue' && <EmailQueuePage accounts={accounts} />}
        {activePage === 'email-jobs' && <EmailJobsPage accounts={accounts} />}
        {activePage === 'job-queue' && <><MissingBillsJobPanel /><BudgetBuildsJobPanel /><JobQueueContent /></>}
        {activePage === 'bill2rollin' && <Bill2RollinCockpit />}
        {activePage === 'refund-pending' && <RefundPendingPage accounts={accounts} />}
      </main>
    </div>
  );
}
